<template>
  <div>
    <div
      v-if="!isInModal"
      :class="{ 'graph-header-card': true, 'graph-header-no-filtering': !dataFilter }"
    >
      <p :style="addMargin && 'margin-bottom: 0;'">{{ title }}</p>
      <div class="header">
        <div class="value">
          <div v-if="value">
            {{ value }}
          </div>
          <slot v-else />
        </div>
        <div v-if="dataFilter" class="date">
          {{ formattedRange }}{{ totalLabel ? ':' : '' }}
          <span v-if="totalLabel" class="total-label">{{ totalLabel }}</span>
        </div>
      </div>
    </div>
    <div v-else class="graph-header-in-modal">
      <span class="title-in-modal">{{ title }}</span>
      <v-icon style="cursor: pointer; user-select: none" @click="$emit('closeModal')">close</v-icon>
    </div>
    <graph-date-filters
      v-if="dataFilter && rangeType === DatePickerRangeType.DATE"
      :title="reportRadioButtonsTitle"
      :checked-report-option="checkedReportOption"
      :date-filter-options="dateFilterOptions"
      :date-range="dateRange"
      :max-date-range="maxDateRange"
      @changeCheckedOption="changeCheckedOption($event)"
      @changeDateRange="changeDateRange"
    />
    <graph-month-filters
      v-else-if="dataFilter && rangeType === DatePickerRangeType.MONTH"
      :title="reportRadioButtonsTitle"
      :checked-report-option="checkedReportOption"
      :date-filter-options="dateFilterOptions"
      :date-range="dateRange"
      :max-date-range="maxDateRange"
      @changeCheckedOption="changeCheckedMonthsOption($event)"
      @changeDateRange="changeDateRange"
    />
  </div>
</template>

<script>
import { formatDate } from '@/utils'
import moment from 'moment'
import 'vue2-datepicker/index.css'
import { DatePickerRangeType } from '@/types'
import GraphDateFilters from '@/components/shared/graphs/graph-date-filters.vue'
import GraphMonthFilters from '@/components/shared/graphs/graph-month-filters.vue'

export default {
  name: 'graph-header',
  components: { GraphMonthFilters, GraphDateFilters },
  props: {
    title: String,
    value: String,
    dataFilter: Boolean,
    dateFilterOptions: Array,
    initialDateRangeOption: Number,
    initialDateRange: Array,
    isInModal: Boolean,
    reportRadioButtonsTitle: String,
    maxDateRange: Number,
    rangeType: {
      type: String,
      default: DatePickerRangeType.DATE,
    },
    totalLabel: String,
  },

  data() {
    return {
      addMargin: !this.value && !this.dataFilter,
      checkedReportOption: this.initialDateRangeOption || 1,
      dateRange: this.getInitialDateRange(),
    }
  },
  computed: {
    DatePickerRangeType() {
      return DatePickerRangeType
    },
    formattedRange() {
      const format = this.rangeType === DatePickerRangeType.MONTH ? 'YY\'/MM' : 'DD/MM'

      return `${formatDate(this.dateRange[0], format)} - ${formatDate(this.dateRange[1], format)}`
    },
  },
  watch: {
    dateRange() {
      if (this.checkedReportOption === 3) {
        this.$emit('changeDateRange', { dateRange: this.dateRange, isCustom: true })
      }
    },
  },

  methods: {
    formatDate,
    changeCheckedOption(value) {
      this.checkedReportOption = value
      if (![1,2,4].includes(value)) return

      let daysToSubtract = 0
      switch (value) {
      case 1:
        daysToSubtract = 6
        break
      case 2:
        daysToSubtract = 13
        break
      case 4:
        daysToSubtract = 20
        break
      }

      this.dateRange = [moment().subtract(daysToSubtract, 'd').startOf('day'), moment().endOf('day')]
      this.$emit('changeDateRange', { dateRange: this.dateRange, isCustom: false })
    },
    changeDateRange(value) {
      this.dateRange = value
    },

    changeCheckedMonthsOption(value) {
      this.checkedReportOption = value
      if (![1,2].includes(value)) return

      let monthsToSubtract = value === 2 ? 11 : 5

      this.dateRange = [moment().subtract(monthsToSubtract, 'month').startOf('month'), moment().endOf('month')]
      this.$emit('changeDateRange', { dateRange: this.dateRange, isCustom: false })
    },
    getInitialDateRange() {
      if (this.initialDateRange) {
        return this.initialDateRange
      }

      if (this.rangeType === DatePickerRangeType.MONTH) {
        return [moment().subtract(5, 'month').toDate(), moment().toDate()]
      }

      return [moment().subtract(6, 'day').toDate(), moment().toDate()]
    },
  },
}
</script>

<style>
.graph-header-card {
  background-color: #f5f5f5;
  padding: 16px;
  border-top-left-radius: 7px;
  border-top-right-radius: 7px;
}

.graph-header-no-filtering {
  margin-bottom: 54px;
  height: fit-content;
}

.header {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-items: space-between;
}

.graph-header-card p {
  font-family: 'Raleway';
  font-style: normal;
  font-weight: 600;
  font-size: 20px;
  line-height: 120%;
  letter-spacing: -0.03em;
  font-feature-settings: 'pnum' on, 'lnum' on;
  color: #666666;
}
.graph-header-card .date {
  font-family: Source Sans Pro;
  font-weight: 400;
  font-size: 20px;
  line-height: 30px;
  color: #666666;
}

.graph-header-card .value {
  font-family: 'Raleway';
  font-style: normal;
  font-weight: 600;
  font-size: 20px;
  line-height: 120%;
  flex: 1;
  letter-spacing: -0.03em;
  font-feature-settings: 'pnum' on, 'lnum' on;
  color: #0277bd;
}

.graph-header-in-modal {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  align-self: stretch;
}

.title-in-modal {
  color: #0277bd;
  font-variant-numeric: lining-nums proportional-nums;
  font-family: Raleway;
  font-size: 1.25rem;
  font-style: normal;
  font-weight: 600;
  line-height: 120%;
  letter-spacing: -0.0375rem;
  flex: 1;
}

.total-label {
  color: #0277BD;
  font-family: "Source Sans Pro";
  font-size: 1.125rem;
  font-style: normal;
  font-weight: 700;
  line-height: 150%;
}
</style>
